import { EnhancedDataTable, type ColumnConfig } from '@/components/tables/enhanced-data-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useCalendarEventsQuery } from '@/features/calendar/api/useCalendarEventsQuery'
import { ClientContainer } from '@/features/calendar/components/client-container'
import { EventDetailsDialog } from '@/features/calendar/components/dialogs/event-details-dialog'
import { CalendarProvider } from '@/features/calendar/contexts/calendar-context'
import type { ICalendarEvent } from '@/features/calendar/interfaces'
import {
  IconCalendarBolt,
  IconCalendarEvent,
  IconGrid3x3,
  IconList
} from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import { format, isSameDay, parseISO } from 'date-fns'
import { useMemo, useState } from 'react'

function CalendarPage() {
  const eventsQuery = useCalendarEventsQuery()
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')

  if (eventsQuery.isError) {
    return (
      <div
        role="alert"
        className="border-destructive/40 bg-destructive/10 text-destructive flex flex-col gap-2 rounded-md border p-4"
      >
        <p className="font-semibold">Unable to load events</p>
        <p className="text-sm">
          {eventsQuery.error?.message ?? 'We could not reach the events service just yet. Try again shortly.'}
        </p>
      </div>
    )
  }

  const events = eventsQuery.data ?? []

  const tableColumns = useMemo<ColumnConfig<ICalendarEvent>[]>(() => {
    const resolveCategoryBadge = (category: string) => {
      switch (category) {
        case 'training':
          return 'bg-blue-100 text-blue-800 border-blue-200'
        case 'webinar':
          return 'bg-purple-100 text-purple-800 border-purple-200'
        case 'networking':
          return 'bg-green-100 text-green-800 border-green-200'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200'
      }
    }

    const renderSchedule = (event: ICalendarEvent) => {
      const start = safeParseDate(event.startAt)
      const end = safeParseDate(event.endAt)

      if (!start || !end) {
        return <span className="text-muted-foreground">Schedule to be confirmed</span>
      }

      if (isSameDay(start, end)) {
        return (
          <div className="flex flex-col">
            <span className="font-medium">{format(start, 'EEE, MMM d, yyyy')}</span>
            <span className="text-xs text-muted-foreground">
              {format(start, 'h:mm a')}
              {' – '}
              {format(end, 'h:mm a')}
            </span>
          </div>
        )
      }

      return (
        <div className="flex flex-col">
          <span className="font-medium">{format(start, 'EEE, MMM d, yyyy • h:mm a')}</span>
          <span className="text-xs text-muted-foreground">
            →
            {' '}
            {format(end, 'EEE, MMM d, yyyy • h:mm a')}
          </span>
        </div>
      )
    }

    return [
      {
        key: 'title',
        header: 'Event',
        accessor: (row: ICalendarEvent) => row.title,
        sortable: true,
        width: '32%',
        cell: (_value: unknown, row: ICalendarEvent) => (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-medium leading-tight">{row.title}</span>
              {row.isFeatured && (
                <Badge variant="outline" className="border-primary/50 bg-primary/10 text-primary">
                  Featured
                </Badge>
              )}
            </div>
            {row.description && (
              <p className="text-xs text-muted-foreground line-clamp-2">{row.description}</p>
            )}
          </div>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        accessor: (row: ICalendarEvent) => row.category,
        sortable: true,
        filterable: true,
        width: '12%',
        cell: (_value: unknown, row: ICalendarEvent) => (
          <Badge className={resolveCategoryBadge(row.category)}>{row.category}</Badge>
        ),
      },
      {
        key: 'schedule',
        header: 'Schedule',
        accessor: (row: ICalendarEvent) => row.startAt,
        sortable: true,
        width: '24%',
        cell: (_value: unknown, row: ICalendarEvent) => renderSchedule(row),
      },
      {
        key: 'location',
        header: 'Location',
        accessor: (row: ICalendarEvent) => row.location,
        sortable: true,
        width: '18%',
        cell: (_value: unknown, row: ICalendarEvent) => (
          <span className="text-sm">{row.location || (row.isOnline ? 'Online' : 'To be confirmed')}</span>
        ),
      },
      {
        key: 'registrations',
        header: 'Registrations',
        accessor: (row: ICalendarEvent) =>
          typeof row.registrations === 'number' ? row.registrations : -1,
        sortable: true,
        align: 'center',
        width: '7%',
        cell: (_value: unknown, row: ICalendarEvent) =>
          typeof row.registrations === 'number' ? row.registrations : '—',
      },
      {
        key: 'availableSpots',
        header: 'Spots remaining',
        accessor: (row: ICalendarEvent) =>
          typeof row.availableSpots === 'number' ? row.availableSpots : -1,
        sortable: true,
        align: 'center',
        width: '7%',
        cell: (_value: unknown, row: ICalendarEvent) =>
          typeof row.availableSpots === 'number' ? Math.max(row.availableSpots, 0) : '—',
      },
      {
        key: 'points',
        header: 'CPD points',
        accessor: (row: ICalendarEvent) => (typeof row.points === 'number' ? row.points : 0),
        sortable: true,
        align: 'center',
        width: '7%',
        cell: (_value: unknown, row: ICalendarEvent) =>
          typeof row.points === 'number' ? row.points : '—',
      },
      {
        key: 'actions',
        header: 'Actions',
        sortable: false,
        align: 'center',
        width: '10%',
        cell: (_value: unknown, row: ICalendarEvent) => (
          <EventDetailsDialog
            training={row}
          >
            <Button size="sm" variant="outline">
              View
            </Button>
          </EventDetailsDialog>
        ),
      },
    ]
  }, [])

  const isTableLoading = eventsQuery.isPending || eventsQuery.isFetching

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <IconCalendarBolt className="text-primary size-8" aria-hidden />
          <div>
            <h1 className="text-2xl font-semibold">Training & Event Calendar</h1>
            <p className="text-muted-foreground text-sm">
              Discover upcoming trainings, webinars, and member networking events.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            <IconGrid3x3 className="size-4" />
            Calendar
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <IconList className="size-4" />
            List
          </Button>
        </div>
      </div>

      <Card className="border-muted">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <IconCalendarEvent className="text-primary size-6" aria-hidden />
            {viewMode === 'calendar' ? 'Calendar View' : 'Upcoming Events'}
          </CardTitle>
          <CardDescription>
            {viewMode === 'calendar'
              ? 'Navigate through dates to view scheduled trainings and events.'
              : 'Complete list of upcoming trainings, webinars, and networking events.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CalendarProvider trainings={events}>
            {viewMode === 'calendar' ? (
              <ClientContainer />
            ) : (
              <EnhancedDataTable
                data={events}
                columns={tableColumns}
                enablePagination
                enableSorting
                enableGlobalFilter
                enableColumnFilters
                enableExport
                pageSize={20}
                initialColumnVisibility={{
                  registrations: false,
                  availableSpots:false
                }}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                searchPlaceholder="Search events…"
                loading={isTableLoading}
                emptyStateMessage="No upcoming events found."
              />
            )}
          </CalendarProvider>
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/benefits/calendar')({
  component: CalendarPage,
})

function safeParseDate(value: string | undefined): Date | undefined {
  if (!value) return undefined
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}
